import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { Button } from '../../components/ui/button';
import { Copy, RefreshCw, Check } from 'lucide-react';

const CHARS = {
  upper: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower: 'abcdefghijkmnpqrstuvwxyz',
  digits: '23456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

const PasswordCalculator = () => {
  const [length, setLength] = useState(16);
  const [useUpper, setUseUpper] = useState(true);
  const [useLower, setUseLower] = useState(true);
  const [useDigits, setUseDigits] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = () => {
    let pool = '';
    if (useUpper) pool += CHARS.upper;
    if (useLower) pool += CHARS.lower;
    if (useDigits) pool += CHARS.digits;
    if (useSymbols) pool += CHARS.symbols;
    if (!pool) { setPassword(''); return; }
    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    let p = '';
    for (let i = 0; i < length; i++) p += pool[arr[i] % pool.length];
    setPassword(p);
    setCopied(false);
  };

  React.useEffect(() => { generate(); /* initial generation */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard failed silently
    }
  };

  const strength = useMemo(() => {
    if (!password) return { score: 0, label: '—', color: 'bg-slate-300', text: 'text-slate-500' };
    const classes = [useUpper, useLower, useDigits, useSymbols].filter(Boolean).length;
    const entropyBits = Math.log2(Math.pow((useUpper ? 24 : 0) + (useLower ? 24 : 0) + (useDigits ? 8 : 0) + (useSymbols ? 26 : 0) || 1, length));
    if (entropyBits < 40) return { score: 25, label: 'Weak', color: 'bg-red-500', text: 'text-red-600', entropyBits };
    if (entropyBits < 60) return { score: 50, label: 'Fair', color: 'bg-amber-500', text: 'text-amber-700', entropyBits };
    if (entropyBits < 80) return { score: 75, label: 'Strong', color: 'bg-indgreen-500', text: 'text-indgreen-700', entropyBits };
    return { score: 100, label: 'Very Strong', color: 'bg-indgreen-600', text: 'text-indgreen-700', entropyBits };
  }, [password, useUpper, useLower, useDigits, useSymbols, length]);

  return (
    <CalculatorLayout
      title="Password Generator"
      subtitle="Generate cryptographically secure random passwords right in your browser."
      category="Other"
      description={<>
        <p>Create strong, random passwords using your browser's secure random number generator. Nothing is sent to a server — all generation happens locally and privately.</p>
        <p>For maximum security, use at least 16 characters with a mix of uppercase, lowercase, digits, and symbols.</p>
      </>}
      formulaNote="Entropy (bits) = log₂(pool_size^length)   •  ≥80 bits is considered cryptographically strong"
    >
      <div className="space-y-6">
        {/* Output */}
        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Generated Password</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-white rounded-lg border border-slate-200 px-4 py-3 display-font text-lg font-bold text-bharat-navy break-all min-h-[52px]">
              {password || <span className="text-slate-400">Select options to generate</span>}
            </div>
            <Button variant="outline" size="icon" onClick={generate} className="h-12 w-12" aria-label="Regenerate">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={copy} className="h-12 bg-saffron-500 hover:bg-saffron-600 text-white" disabled={!password}>
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
            </Button>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">Strength</span>
              <span className={`font-semibold ${strength.text}`}>{strength.label} {strength.entropyBits ? `(${Math.round(strength.entropyBits)} bits)` : ''}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className={`h-full ${strength.color} transition-all`} style={{ width: `${strength.score}%` }} />
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <SliderInput label="Length" value={length} onChange={setLength} min={4} max={64} step={1} />
            <div className="space-y-2">
              <CheckRow label="Uppercase Letters (A–Z)" checked={useUpper} onChange={setUseUpper} />
              <CheckRow label="Lowercase Letters (a–z)" checked={useLower} onChange={setUseLower} />
              <CheckRow label="Digits (2–9)" checked={useDigits} onChange={setUseDigits} />
              <CheckRow label="Symbols (!@#$...)" checked={useSymbols} onChange={setUseSymbols} />
            </div>
            <Button onClick={generate} className="w-full bg-indgreen-500 hover:bg-indgreen-600 text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Generate New Password
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-bharat-navy text-sm mb-3">Password Tips</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>• Use 16+ characters for bank and email accounts</li>
              <li>• Never reuse passwords across sites</li>
              <li>• Consider using a password manager</li>
              <li>• Enable two-factor authentication (2FA)</li>
              <li>• Ambiguous characters (0, O, 1, l) are excluded to reduce errors</li>
            </ul>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const CheckRow = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer py-1.5">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-saffron-500 w-4 h-4" />
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

export default PasswordCalculator;
